import { Tooltip } from "@clockwise/web-commons/src/ui/tooltip";
import { LayersRounded } from "@material-ui/icons";
import classNames from "classnames";
import { every, isEmpty, noop, union } from "lodash";
import { default as React, useMemo } from "react";
import { useSelector } from "react-redux";
import { CalendarIds } from "../../../../../state/actions/multi-calendar.actions";
import { IReduxState } from "../../../../../state/reducers/root.reducer";

// Webserver has a allowance factor that should approximately allow 100 calendars to query at once
// before webserver errors with `"Request too complex` on the `PlannerEventsQuery`
// Thus I'm allowing 50 here to be safe
export const QUERYING_LIMIT_OF_CALENDARS_TO_SHOW_IN_PLANNER = 50;

const isGroupCalendarId = (calendarId: string) => calendarId.includes("@group.calendar.google.com");

export const calculateIfIsAtOrBeyondCalendarLimit = (calendarsAlreadyShowing: CalendarIds) => {
  const allCalendarsToShow = calendarsAlreadyShowing.filter(
    (calendarId) => !isGroupCalendarId(calendarId), // since group calendars are not shown in the planner
  );

  return allCalendarsToShow.length >= QUERYING_LIMIT_OF_CALENDARS_TO_SHOW_IN_PLANNER;
};

export const calculateIfTooManyCalendarsToShow = (
  calendarIdsToAdd: CalendarIds,
  calendarsAlreadyShowing: CalendarIds,
) => {
  const allCalendarsToShow = union(calendarsAlreadyShowing, calendarIdsToAdd).filter(
    (calendarId) => !isGroupCalendarId(calendarId), // since group calendars are not shown in the planner
  );

  return allCalendarsToShow.length > QUERYING_LIMIT_OF_CALENDARS_TO_SHOW_IN_PLANNER;
};

export const TOO_MANY_CALENDARS_TO_SHOW_MESSAGE = "Too many calendars to load at once";
const getReasonForLayersDisabled = (
  calendarIdsToAdd: CalendarIds,
  calendarsAlreadyShowing: CalendarIds,
): string | null => {
  const isTooManyCalendarsToShow = calculateIfTooManyCalendarsToShow(
    calendarIdsToAdd,
    calendarsAlreadyShowing,
  );

  if (isEmpty(calendarIdsToAdd)) {
    return "No attendees calendars to show";
  }

  if (every(calendarIdsToAdd, (calendar) => calendarsAlreadyShowing.includes(calendar))) {
    //  All attendees calendars already shown
    return null;
  }

  return isTooManyCalendarsToShow ? TOO_MANY_CALENDARS_TO_SHOW_MESSAGE : null;
};

export type DisableReason = string | null;
export const useIsLayersDisabledWithReason = (
  calendarIdsToAdd: CalendarIds,
): { disabled: boolean; reason: DisableReason } => {
  const calendarIdsToFullyShow = useSelector(
    (state: IReduxState) => state.multiCalendar.calendarIdsToFullyShow,
  );
  const reaseonToDisableIfAny = useMemo(() => {
    return getReasonForLayersDisabled(calendarIdsToAdd, calendarIdsToFullyShow);
  }, [calendarIdsToFullyShow]);

  return {
    disabled: !isEmpty(reaseonToDisableIfAny),
    reason: reaseonToDisableIfAny,
  };
};

export const ToggleCalendarLayers = ({
  onClick = noop,
  disabled,
  isToggledOn,
  calendarIds = [],
  reasonForDisabled,
}: {
  onClick: () => void;
  disabled: boolean;
  reasonForDisabled?: string | null;
  isToggledOn: boolean;
  calendarIds: CalendarIds;
}) => {
  const tooltipBody =
    reasonForDisabled || (isToggledOn ? "Hide participants overlay" : "Show participants overlay");

  return (
    <Tooltip
      placement="right"
      body={tooltipBody}
      showArrow={false}
      className="cw-flex cw-self-center cw-p-3"
    >
      <div
        className={classNames("cw-flex cw-self-center", {
          "cw-cursor-pointer": !isEmpty(calendarIds),
          "cw-cursor-not-allowed": disabled,
          "cw-opacity-50": disabled,
        })}
        onClick={onClick}
        aria-label={isToggledOn ? "Hide participants overlay" : "Show participants overlay"}
      >
        {isToggledOn ? (
          <LayersRounded htmlColor="#0335ff" />
        ) : (
          <LayersRounded htmlColor="#575757" />
        )}
      </div>
    </Tooltip>
  );
};
